/**
 * TypeScript declaration string describing the globals that user-supplied
 * webhook code is allowed to use inside the sandbox. Added to Monaco as an
 * extra lib (see `lib/monaco.ts`) so the editor provides autocompletion for
 * `$incoming`, `$outcoming`, `emit`, `emitHttp`, `emitInternal`, and `console`.
 *
 * The `console` global is already provided by the DOM lib; we reference it in
 * comments rather than redeclaring it (which would conflict with the DOM types).
 */
export const SANDBOX_DTS = `
/**
 * A single event delivered to the webhook handler. Shape is arbitrary and
 * depends on the trigger (HTTP body, internal-event payload, etc).
 */
interface SandboxEvent {
  [key: string]: unknown;
}

/** Observable wrapper around the incoming event stream. */
interface SandboxIncoming {
  /** Subscribe to incoming events. */
  subscribe(callback: (event: SandboxEvent) => void): void;
  /** Alias for subscribe. */
  on(callback: (event: SandboxEvent) => void): void;
}

/** Stream to which webhook code can emit results. */
interface SandboxOutgoing {
  /** Emit an event downstream. */
  emit(event: unknown): void;
  /** Alias for emit. */
  next(event: unknown): void;
}

/** Read-only wrapper around the incoming event payload. */
declare const $incoming: SandboxIncoming;

/** Wrapper used to emit results from webhook code. */
declare const $outcoming: SandboxOutgoing;

/** Emit an arbitrary downstream event. */
declare function emit(event: unknown): void;

/** Emit an HTTP delivery (perform an outbound HTTP request). */
declare function emitHttp(options: {
  method?: string;
  url: string;
  body?: unknown;
  headers?: Record<string, string>;
}): void;

/** Emit an internal API invocation to another cluster service. */
declare function emitInternal(options: {
  service: string;
  method: string;
  data?: unknown;
}): void;

/**
 * Standard console is available for logging:
 *   console.log(...args)
 *   console.info(...args)
 *   console.warn(...args)
 *   console.error(...args)
 * Captured output (up to sandboxConfig.consoleCaptureBytes) is returned in the
 * SandboxTestResult / DeliveryLog as {@link consoleOutput}.
 */
`;